import { ContentWrapper, SectionWrapper, Subtitle, Title, Wrapper } from './AccountInfo.styled';
import { useDispatch, useSelector } from 'react-redux';

import { AccountRow } from './AccountRow';
import type { EditUserType } from './types/editUserValidations';
import { defaultEditUserType } from './types/editUserValidations';
import { setUser } from '../../store/user/userSlice';
import type { state } from '../../types/store/state';
import { updateUser } from '../../api/users';
import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { handleToastAsyncAction } from '../../utils/handleToastAsyncAction';
import * as Sentry from '@sentry/react';

export const AccountInfo = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const user = useSelector(({ user }: state) => user);
  const [errors, setErrors] = useState<EditUserType>(defaultEditUserType);

  const saveInfo = async (key: string, value: string) => {
    const editableUser = {
      username: user.username,
      firstName: user.firstName,
      lastName: user.lastName,
      phoneNumber: user.phoneNumber,
      email: user.email,
    };

    await handleToastAsyncAction(
      () => updateUser({
        params: {
          ...editableUser,
          [key]: value,
        }
      }),
      {
        onSuccessMessage: t('account.updateInfo.toastify.success'),
        onErrorMessage: t('account.updateInfo.toastify.error'),
        dispatchAction: async () => {
          const editedUser = await updateUser({
            params: {
              ...editableUser,
              [key]: value,
            }
          });
          dispatch(setUser(editedUser));
        },
        errorLogger: (error) => {
          console.error('Error updating user info:', error);
          if (error.response?.status === 400) {
            setErrors({
              ...errors,
              ...error.response.data,
            });
          }
          Sentry.captureException(error);
        },
      }
    );
  };

  return (
    <Wrapper>
      <Title>
        {t('account.title')}
      </Title>
      <SectionWrapper>
        <Subtitle>
          {t('account.information.title')}
        </Subtitle>
        <ContentWrapper>
          <AccountRow
            title={t('account.firstName.title')}
            name="firstName"
            type="string"
            content={user.firstName}
            errors={errors.firstName}
            ctaTitle={t('account.action.change')}
            ctaCallback={saveInfo}
          />
          <AccountRow
            title={t('account.lastName.title')}
            name="lastName"
            type="string"
            content={user.lastName}
            errors={errors.lastName}
            ctaTitle={t('account.action.change')}
            ctaCallback={saveInfo}
          />
          <AccountRow
            title={t('account.userName.title')}
            name="username"
            type="string"
            content={user.username}
            errors={errors.username}
            ctaTitle={t('account.action.change')}
            ctaCallback={saveInfo}
          />
          <AccountRow
            title={t('account.email.title')}
            name="email"
            type="email"
            content={user.email}
            errors={errors.email}
            ctaTitle={t('account.action.change')}
            ctaCallback={saveInfo}
          />
          <AccountRow
            title={t('account.phoneNumber.title')}
            name="phoneNumber"
            type="tel"
            content={user.phoneNumber}
            errors={errors.phoneNumber}
            ctaTitle={t('account.action.change')}
            ctaCallback={saveInfo}
          />
        </ContentWrapper>
      </SectionWrapper>
    </Wrapper>
  );
};
